import React, { useEffect, useState } from "react";
import api from "@/api";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import DatePicker from "@/components/DatePicker";
import TimePicker from "@/components/TimePicker";
import CustomerPicker from "@/components/CustomerPicker";
import {
  compressImage, DELIVERY_TYPES, DELIVERY_PARTNERS, PAYMENT_METHODS, PAYMENT_STATUSES,
  FLAVOURS, TIERS, OCCASIONS, SHAPES, PRIORITY, STATUSES, inr,
} from "@/lib/tps";
import { ChevronLeft, Save, ImagePlus, X, Check, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const CUSTOM_FLAVOUR = "Other / Custom Flavour";
const CUSTOM_OCCASION = "Other";

const emptyItem = {
  occasion: "", theme: "", servings: "", weight: "", shape: "Round", tier: "Single",
  quantity: 1, flavour: "", cake_message: "", customization_notes: "",
};

// Note: we keep NUMERIC fields as strings in state so users can freely clear the "0".
// They are coerced to Number(...) only when calculating balance / submitting.
const blank = {
  customer_name: "", customer_phone: "", customer_email: "", customer_address: "", customer_instagram: "",
  order_date: new Date().toISOString(), required_date: "", required_time: "16:00",
  delivery_type: "Pickup", delivery_method: "Self Pickup",
  delivery_address: "", delivery_tracking_link: "", google_maps_link: "",
  delivery_charges: "", delivery_notes: "",
  items: [{ ...emptyItem }],
  total_price: "", advance_amount: "",
  payment_method: "UPI", payment_status: "Not Paid",
  notes: "", production_notes: "", ingredients_notes: "", staff_notes: "",
  priority: "Normal", final_design_approved: false,
  reference_images: [], reference_image: "",
  final_reference_image: "", final_cake_ready_picture: "", payment_screenshot: "",
  status: "New",
};

function toNum(v) {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function autoPayStatus(total, advance) {
  const t = toNum(total);
  const a = toNum(advance);
  if (a <= 0) return "Not Paid";
  if (a >= t && t > 0) return "Fully Paid";
  return "Advance Paid";
}

export default function OrderForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isOwner } = useAuth();
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [customerMode, setCustomerMode] = useState("picker"); // "picker" | "manual" | "selected"
  const [customOccasion, setCustomOccasion] = useState("");
  const [customFlavour, setCustomFlavour] = useState("");
  const isEdit = Boolean(id);

  // Load existing order for edit
  useEffect(() => {
    if (!id) return;
    api.get(`/orders/${id}`).then((r) => {
      const d = r.data;
      const item = d.items?.[0] || {};
      const knownOccasion = OCCASIONS.includes(item.occasion) && item.occasion !== CUSTOM_OCCASION;
      const knownFlavour = FLAVOURS.includes(item.flavour) && item.flavour !== CUSTOM_FLAVOUR;
      setForm({
        ...blank,
        ...d,
        // convert numeric fields back to strings for the controlled inputs
        total_price: d.total_price != null ? String(d.total_price) : "",
        advance_amount: d.advance_amount != null ? String(d.advance_amount) : "",
        delivery_charges: d.delivery_charges != null ? String(d.delivery_charges) : "",
        items: d.items?.length ? d.items.map((it) => ({ ...emptyItem, ...it })) : [{ ...emptyItem }],
        reference_images: d.reference_images || (d.reference_image ? [d.reference_image] : []),
      });
      // Populate custom fields when the stored value is not in the known list
      if (item.occasion && !knownOccasion) {
        setCustomOccasion(item.occasion);
        // internally we still show "Other" as the selected dropdown option
        setForm((f) => ({ ...f, items: [{ ...f.items[0], occasion: CUSTOM_OCCASION }] }));
      }
      if (item.flavour && !knownFlavour) {
        setCustomFlavour(item.flavour);
        setForm((f) => ({ ...f, items: [{ ...f.items[0], flavour: CUSTOM_FLAVOUR }] }));
      }
      setCustomerMode("selected");
    });
  }, [id]);

  // Prefill from extract handoff
  useEffect(() => {
    if (id) return;
    const p = new URLSearchParams(window.location.search);
    if (p.get("from") === "extract") {
      try {
        const data = JSON.parse(sessionStorage.getItem("tps_extract_payload") || "null");
        if (data) {
          setForm((f) => ({
            ...f, ...data,
            total_price: data.total_price ? String(data.total_price) : "",
            advance_amount: data.advance_amount ? String(data.advance_amount) : "",
            delivery_charges: data.delivery_charges ? String(data.delivery_charges) : "",
            items: [{ ...emptyItem, ...(data.items?.[0] || {}) }],
            reference_images: data.reference_images || (data.reference_image ? [data.reference_image] : []),
          }));
          sessionStorage.removeItem("tps_extract_payload");
          setCustomerMode("manual");
        }
      } catch (e) { /* ignore */ }
    }
  }, [id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateItem = (k, v) => setForm((f) => ({ ...f, items: [{ ...f.items[0], [k]: v }] }));

  const balance = Math.max(0, toNum(form.total_price) - toNum(form.advance_amount));
  const autoStatus = autoPayStatus(form.total_price, form.advance_amount);

  useEffect(() => {
    setForm((f) => ({ ...f, payment_status: autoPayStatus(f.total_price, f.advance_amount) }));
    // eslint-disable-next-line
  }, [form.total_price, form.advance_amount]);

  const applyCustomer = (c) => {
    setForm((f) => ({
      ...f,
      customer_name: c.name || "",
      customer_phone: c.phone || "",
      customer_email: c.email || "",
      customer_address: c.address || "",
      customer_instagram: c.instagram || "",
      // Prefill google_maps_link from customer profile if not already set
      google_maps_link: f.google_maps_link || c.google_maps_link || "",
      notes: c.notes ? `Customer note: ${c.notes}\n\n${f.notes || ""}`.trim() : f.notes,
    }));
    setCustomerMode("selected");
  };

  const startManual = () => {
    setForm((f) => ({
      ...f,
      customer_name: "", customer_phone: "", customer_email: "",
      customer_address: "", customer_instagram: "",
    }));
    setCustomerMode("manual");
  };

  const backToPicker = () => setCustomerMode("picker");

  const addRefImage = async (e) => {
    const files = Array.from(e.target.files || []);
    const result = [];
    for (const f of files) {
      try { result.push(await compressImage(f, 1400, 0.82)); } catch (err) { /* skip bad image */ }
    }
    if (result.length === 0) { toast.error("Could not upload images."); e.target.value = ""; return; }
    setForm((f) => ({ ...f, reference_images: [...(f.reference_images || []), ...result] }));
    e.target.value = "";
  };
  const removeRefImage = (i) => setForm((f) => ({ ...f, reference_images: f.reference_images.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_phone || !form.required_date) {
      toast.error("Customer name, phone and required date are needed");
      return;
    }
    // Resolve custom occasion / flavour
    const finalOccasion = form.items[0].occasion === CUSTOM_OCCASION ? (customOccasion.trim() || CUSTOM_OCCASION) : form.items[0].occasion;
    const finalFlavour = form.items[0].flavour === CUSTOM_FLAVOUR ? (customFlavour.trim() || CUSTOM_FLAVOUR) : form.items[0].flavour;

    setSaving(true);
    try {
      const payload = {
        ...form,
        // coerce numeric fields
        total_price: toNum(form.total_price),
        advance_amount: toNum(form.advance_amount),
        delivery_charges: toNum(form.delivery_charges),
        items: [{ ...form.items[0], occasion: finalOccasion, flavour: finalFlavour }],
        reference_image: form.reference_images?.[0] || form.reference_image || "",
      };
      if (isEdit) {
        await api.patch(`/orders/${id}`, payload);
        toast.success("Order updated");
        nav(`/orders/${id}`);
      } else {
        const { data } = await api.post("/orders", payload);
        toast.success(`Order ${data.order_id} created`);
        nav(`/orders/${data.id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const isDelivery = form.delivery_type === "Delivery";
  const showOccasionInput = form.items[0].occasion === CUSTOM_OCCASION;
  const showFlavourInput = form.items[0].flavour === CUSTOM_FLAVOUR;

  return (
    <form onSubmit={submit} className="space-y-4 animate-fade-in" data-testid="order-form">
      <div className="flex items-center justify-between sticky top-0 z-20 bg-[#FDFBF7]/95 backdrop-blur py-2 -mx-3 px-3 md:mx-0 md:px-0">
        <Button type="button" variant="ghost" onClick={() => nav(-1)} className="h-8" data-testid="order-form-back">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h1 className="font-heading text-xl md:text-2xl">{isEdit ? "Edit Order" : "New Order"}</h1>
        <Button type="submit" disabled={saving} className="bg-[#B86F77] hover:bg-[#A35D65] h-9" data-testid="order-save-btn">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : (isEdit ? "Save" : "Create")}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* LEFT column */}
        <div className="lg:col-span-2 space-y-3">
          {/* Customer Section */}
          <Card className="border-[#E8E3DF]">
            <CardContent className="p-4 space-y-3">
              <SectionTitle>Customer Details</SectionTitle>

              {customerMode === "picker" && !isEdit && (
                <div className="space-y-2">
                  <CustomerPicker onSelect={applyCustomer} onManual={startManual} />
                  <div className="text-[11px] text-[#4B5563]">
                    Search a repeat customer, or click <em>Enter New Customer Manually</em> in the results.
                  </div>
                </div>
              )}

              {(customerMode === "selected" || customerMode === "manual" || isEdit) && (
                <>
                  {customerMode === "selected" && !isEdit && (
                    <div className="flex items-center justify-between rounded-md bg-[#F3E8E9] border border-[#E8E3DF] px-3 py-2 text-sm" data-testid="selected-customer-banner">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-[#B86F77]" />
                        Using existing customer: <strong>{form.customer_name}</strong>
                      </div>
                      <button type="button" onClick={backToPicker} className="text-[#B86F77] text-xs hover:underline flex items-center gap-1" data-testid="customer-change-btn">
                        <Pencil className="h-3 w-3" /> Change
                      </button>
                    </div>
                  )}
                  {customerMode === "manual" && !isEdit && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#4B5563]">Entering new customer manually.</span>
                      <button type="button" onClick={backToPicker} className="text-[#B86F77] text-xs hover:underline" data-testid="customer-search-again-btn">Search existing instead</button>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Customer Name *">
                      <Input value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} className="h-8" data-testid="cust-name-input" />
                    </Field>
                    <Field label="Phone Number *">
                      <Input value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} className="h-8" data-testid="cust-phone-input" inputMode="tel" />
                    </Field>
                    <Field label="Email Address">
                      <Input type="email" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} className="h-8" data-testid="cust-email-input" />
                    </Field>
                    <Field label="Instagram Handle">
                      <Input value={form.customer_instagram} onChange={(e) => update("customer_instagram", e.target.value)} className="h-8" data-testid="cust-instagram-input" placeholder="@handle" />
                    </Field>
                    <Field label="Address" className="md:col-span-2">
                      <Input value={form.customer_address} onChange={(e) => update("customer_address", e.target.value)} className="h-8" data-testid="cust-address-input" />
                    </Field>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card className="border-[#E8E3DF]">
            <CardContent className="p-4 space-y-3">
              <SectionTitle>Order Date & Time</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Order Date">
                  <DatePicker
                    value={form.order_date && form.order_date.length === 10 ? form.order_date : form.order_date?.slice(0, 10)}
                    onChange={(v) => update("order_date", v)} testId="order-date" />
                </Field>
                <Field label="Required Date *">
                  <DatePicker value={form.required_date} onChange={(v) => update("required_date", v)} testId="required-date" />
                </Field>
                <Field label="Required Time *">
                  <TimePicker value={form.required_time} onChange={(v) => update("required_time", v)} testId="required-time" />
                </Field>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E8E3DF]">
            <CardContent className="p-4 space-y-3">
              <SectionTitle>Cake Details</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Occasion">
                  <Select value={form.items[0].occasion || "__none__"} onValueChange={(v) => updateItem("occasion", v === "__none__" ? "" : v)}>
                    <SelectTrigger className="h-8" data-testid="occasion-select"><SelectValue placeholder="Pick occasion" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {OCCASIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                {showOccasionInput && (
                  <Field label="Enter Occasion">
                    <Input value={customOccasion} onChange={(e) => setCustomOccasion(e.target.value)} className="h-8" data-testid="custom-occasion-input" placeholder="Type the occasion" />
                  </Field>
                )}
                <Field label="Theme">
                  <Input value={form.items[0].theme} onChange={(e) => updateItem("theme", e.target.value)} className="h-8" data-testid="theme-input" placeholder="e.g. Floral, Princess, Marvel..." />
                </Field>
                <Field label="Number of Servings">
                  <Input value={form.items[0].servings} onChange={(e) => updateItem("servings", e.target.value)} className="h-8" data-testid="servings-input" placeholder="e.g. 10–12" />
                </Field>
                <Field label="Cake Weight (KG)">
                  <Input value={form.items[0].weight} onChange={(e) => updateItem("weight", e.target.value)} className="h-8" data-testid="weight-input" placeholder="e.g. 1, 1.5, 2" />
                </Field>
                <Field label="Cake Shape">
                  <Select value={form.items[0].shape || "Round"} onValueChange={(v) => updateItem("shape", v)}>
                    <SelectTrigger className="h-8" data-testid="shape-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SHAPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Tier">
                  <Select value={form.items[0].tier || "Single"} onValueChange={(v) => updateItem("tier", v)}>
                    <SelectTrigger className="h-8" data-testid="tier-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Quantity">
                  <Input type="number" min="1" value={form.items[0].quantity} onChange={(e) => updateItem("quantity", parseInt(e.target.value || 1))} className="h-8" data-testid="qty-input" />
                </Field>
                <Field label="Flavour">
                  <Select value={form.items[0].flavour || "__none__"} onValueChange={(v) => updateItem("flavour", v === "__none__" ? "" : v)}>
                    <SelectTrigger className="h-8" data-testid="flavour-select"><SelectValue placeholder="Choose flavour" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {FLAVOURS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                {showFlavourInput && (
                  <Field label="Enter Flavour">
                    <Input value={customFlavour} onChange={(e) => setCustomFlavour(e.target.value)} className="h-8" data-testid="custom-flavour-input" placeholder="Type the flavour" />
                  </Field>
                )}
                <Field label="Cake Message / Tag Message" className="md:col-span-2">
                  <Input value={form.items[0].cake_message} onChange={(e) => updateItem("cake_message", e.target.value)} className="h-8" data-testid="cake-msg-input" placeholder='e.g. "Happy Birthday, Ananya"' />
                </Field>
                <Field label="Customisation Notes" className="md:col-span-2">
                  <Textarea rows={2} value={form.items[0].customization_notes} onChange={(e) => updateItem("customization_notes", e.target.value)} data-testid="custom-notes-input" />
                </Field>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E8E3DF]">
            <CardContent className="p-4 space-y-3">
              <SectionTitle>Delivery Details</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Delivery Type">
                  <Select value={form.delivery_type} onValueChange={(v) => update("delivery_type", v)}>
                    <SelectTrigger className="h-8" data-testid="delivery-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{DELIVERY_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Delivery Partner">
                  <Select value={form.delivery_method} onValueChange={(v) => update("delivery_method", v)}>
                    <SelectTrigger className="h-8" data-testid="delivery-partner-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{DELIVERY_PARTNERS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Delivery Address" className="md:col-span-2">
                  <Input value={form.delivery_address} onChange={(e) => update("delivery_address", e.target.value)} className="h-8" data-testid="delivery-address-input" disabled={!isDelivery} />
                </Field>
                <Field label="Google Maps Link" className="md:col-span-2">
                  <Input type="url" value={form.google_maps_link} onChange={(e) => update("google_maps_link", e.target.value)} className="h-8" data-testid="google-maps-input" placeholder="https://maps.google.com/…" />
                </Field>
                <Field label="Delivery Tracking Link">
                  <Input value={form.delivery_tracking_link} onChange={(e) => update("delivery_tracking_link", e.target.value)} className="h-8" data-testid="delivery-tracking-input" placeholder="https://…" disabled={!isDelivery} />
                </Field>
                <Field label="Delivery Charges (₹)" className={isOwner ? "" : "hidden"}>
                  <Input
                    type="number" min="0" step="0.01"
                    value={form.delivery_charges}
                    onChange={(e) => update("delivery_charges", e.target.value)}
                    className="h-8" data-testid="delivery-charges-input"
                    disabled={!isDelivery}
                    placeholder="0"
                  />
                </Field>
                <Field label="Delivery Notes" className="md:col-span-2">
                  <Textarea rows={2} value={form.delivery_notes} onChange={(e) => update("delivery_notes", e.target.value)} data-testid="delivery-notes-input" />
                </Field>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E8E3DF]">
            <CardContent className="p-4 space-y-3">
              <SectionTitle>Internal Notes</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Order Status">
                  <Select value={form.status} onValueChange={(v) => update("status", v)}>
                    <SelectTrigger className="h-8" data-testid="order-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Priority">
                  <Select value={form.priority} onValueChange={(v) => update("priority", v)}>
                    <SelectTrigger className="h-8" data-testid="priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITY.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Production Notes" className="md:col-span-2">
                  <Textarea rows={2} value={form.production_notes} onChange={(e) => update("production_notes", e.target.value)} data-testid="production-notes-input" />
                </Field>
                <Field label="Ingredients / Prep Notes" className="md:col-span-2">
                  <Textarea rows={2} value={form.ingredients_notes} onChange={(e) => update("ingredients_notes", e.target.value)} data-testid="ingredients-notes-input" />
                </Field>
                <Field label="Staff Notes" className="md:col-span-2">
                  <Textarea rows={2} value={form.staff_notes} onChange={(e) => update("staff_notes", e.target.value)} data-testid="staff-notes-input" />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-sm pt-1">
                <Checkbox checked={form.final_design_approved} onCheckedChange={(v) => update("final_design_approved", !!v)} data-testid="final-design-cb" />
                Final Design Approved
              </label>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT column: pricing + images */}
        <div className="space-y-3">
          {isOwner && (
          <Card className="border-[#E8E3DF]">
            <CardContent className="p-4 space-y-3">
              <SectionTitle>Payment Details</SectionTitle>
              <Field label="Total Price (₹)">
                <Input
                  type="number" min="0" step="0.01"
                  value={form.total_price}
                  onChange={(e) => update("total_price", e.target.value)}
                  className="h-8" data-testid="total-price-input"
                  placeholder="0"
                />
              </Field>
              <Field label="Advance Paid (₹)">
                <Input
                  type="number" min="0" step="0.01"
                  value={form.advance_amount}
                  onChange={(e) => update("advance_amount", e.target.value)}
                  className="h-8" data-testid="advance-input"
                  placeholder="0"
                />
              </Field>
              <div className="flex items-center justify-between bg-[#FCF9F9] rounded-md p-2.5 border border-[#E8E3DF]">
                <span className="text-xs text-[#4B5563]">Balance Amount</span>
                <span className="font-heading text-lg text-[#B86F77]" data-testid="balance-display">{inr(balance)}</span>
              </div>
              <Field label="Payment Method">
                <Select value={form.payment_method} onValueChange={(v) => update("payment_method", v)}>
                  <SelectTrigger className="h-8" data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Payment Status (auto)">
                <Select value={form.payment_status} onValueChange={(v) => update("payment_status", v)}>
                  <SelectTrigger className="h-8" data-testid="pay-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_STATUSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="text-[11px] text-[#4B5563]">Auto-detected: <strong>{autoStatus}</strong></div>
            </CardContent>
          </Card>
          )}

          <Card className="border-[#E8E3DF]">
            <CardContent className="p-4 space-y-3">
              <SectionTitle>Images</SectionTitle>
              <div>
                <Label className="text-xs text-[#4B5563]">Reference Images (multiple)</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {(form.reference_images || []).map((b, i) => (
                    <div key={i} className="relative">
                      <img src={b} alt="" className="h-20 w-20 object-cover rounded-md border border-[#E8E3DF]" data-testid={`ref-image-${i}`} />
                      <button type="button" onClick={() => removeRefImage(i)} className="absolute -top-1.5 -right-1.5 bg-white border border-[#E8E3DF] rounded-full p-0.5 shadow" data-testid={`ref-image-remove-${i}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <label className="inline-flex items-center justify-center gap-1 h-20 w-20 border border-dashed border-[#E8E3DF] rounded-md cursor-pointer hover:bg-[#FCF9F9] text-xs text-[#4B5563]" data-testid="ref-images-upload">
                    <ImagePlus className="h-4 w-4" />
                    <input type="file" accept="image/*" multiple hidden onChange={addRefImage} />
                  </label>
                </div>
              </div>
              <ImageField label="Final Reference Image" value={form.final_reference_image} onChange={(b) => update("final_reference_image", b)} testId="final-ref-image" />
              <ImageField label="Final Cake Ready Picture" value={form.final_cake_ready_picture} onChange={(b) => update("final_cake_ready_picture", b)} testId="cake-ready-image" />
              <ImageField label="Payment Screenshot" value={form.payment_screenshot} onChange={(b) => update("payment_screenshot", b)} testId="payment-ss" />
            </CardContent>
          </Card>
        </div>
      </div>
    </form>
  );
}

function SectionTitle({ children }) {
  return <h2 className="font-heading text-base text-[#141E30]">{children}</h2>;
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-[#4B5563]">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ImageField({ label, value, onChange, testId }) {
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) { e.target.value = ""; return; }
    try {
      const b = await compressImage(f, 1400, 0.82);
      onChange(b);
    } catch (err) {
      toast.error("Could not upload image. Try another file.");
    }
    e.target.value = "";
  };
  return (
    <div>
      <Label className="text-xs text-[#4B5563]">{label}</Label>
      <div className="mt-1">
        {value ? (
          <div className="relative inline-block">
            <img src={value} alt={label} className="h-24 w-24 object-cover rounded-md border border-[#E8E3DF]" data-testid={`${testId}-preview`} />
            <button type="button" onClick={() => onChange("")} className="absolute -top-1.5 -right-1.5 bg-white border border-[#E8E3DF] rounded-full p-0.5 shadow" data-testid={`${testId}-clear`}>
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-1.5 h-8 px-3 border border-dashed border-[#E8E3DF] rounded-md cursor-pointer hover:bg-[#FCF9F9] text-xs text-[#4B5563]" data-testid={`${testId}-upload`}>
            <ImagePlus className="h-3.5 w-3.5" /> Upload
            <input type="file" accept="image/*" hidden onChange={handle} />
          </label>
        )}
      </div>
    </div>
  );
}
